import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { useState } from "react"
import ContactForm from "components/ContactForm"
import Img from "components/Img"
import { H2 } from "components/Typography"
import { richTextToHtml } from "utils/richTextToHtml"
import * as css from "./ContactSection.module.css"
import dataVisualizationIllustration from "./data-visualization.jpg"
import Overflow from "components/Layout/Overflow"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"
import Image from "components/ui/Image"

function ContactSection({
  label,
  heading,
  headingImage,
  hubspotForm,
  leftSideRichText,
  leftSideContactMessage,
  successMessage,
  logosSectionHeading,
  logos,
  requireJobTitle,
}) {
  const [submitted, setSubmitted] = useState(false)
  const sucessMsgDoc = successMessage?.raw
    ? JSON.parse(successMessage?.raw)
    : null
  const leftSideRichTextDoc = leftSideRichText?.raw
    ? JSON.parse(leftSideRichText?.raw)
    : null
  const leftSideContactMessageDoc = leftSideContactMessage?.raw
    ? JSON.parse(leftSideContactMessage?.raw)
    : null

  const renderHeader = () => (
    <SectionHeader
      level="h1"
      label={label}
      title={heading}
      constrained
      margin
    />
  )

  return (
    <Section>
      <img
        className={css.background}
        src={dataVisualizationIllustration}
        alt="Background"
      />
      {headingImage ? (
        <div className="flex flex-col lg:flex-row">
          {renderHeader()}
          <div className="-mt-2 mb-5 md:-mt-10 lg:ml-auto lg:mt-0 lg:max-w-[20%]">
            <Image src={headingImage} />
          </div>
        </div>
      ) : (
        renderHeader()
      )}
      <div className={css.segment}>
        <div className="grid grid-cols-1 md:grid-cols-[5fr_11fr] gap-8">
          <div className={css.infoColumn}>
            {leftSideRichTextDoc && (
              <div
                className={css.leftSideMessage}
                dangerouslySetInnerHTML={{
                  __html: richTextToHtml(leftSideRichTextDoc),
                }}
              />
            )}
            {leftSideContactMessageDoc && (
              <>
                <hr className={css.divider} />
                <div
                  className={css.contactMessage}
                  dangerouslySetInnerHTML={{
                    __html: richTextToHtml(leftSideContactMessageDoc),
                  }}
                />
              </>
            )}
          </div>
          <div className={css.formColumn}>
            {submitted ? (
              <div
                className={css.message}
                dangerouslySetInnerHTML={{
                  __html: richTextToHtml(sucessMsgDoc),
                }}
              />
            ) : (
              hubspotForm && (
                <ContactForm
                  formId={hubspotForm.formId}
                  trackSubmit={hubspotForm.trackSubmit}
                  onSubmit={() => setSubmitted(true)}
                  requireJobTitle={requireJobTitle}
                />
              )
            )}
          </div>
        </div>
      </div>
      {logos && (
        <Overflow className={css.logos}>
          <H2>{logosSectionHeading}</H2>
          <div className={css.logoWrapper}>
            {logos.map((logo) => (
              <div key={logo.id}>
                <Img
                  image={logo}
                  className={css.imageWrapper}
                  title={logo.description}
                />
              </div>
            ))}
          </div>
        </Overflow>
      )}
    </Section>
  )
}

ContactSection.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  hubspotForm: PropTypes.shape({
    formId: PropTypes.string.isRequired,
    trackSubmit: PropTypes.string,
  }),
  leftSideRichText: PropTypes.shape({
    raw: PropTypes.string,
  }),
  leftSideContactMessage: PropTypes.shape({
    raw: PropTypes.string,
  }),
  successMessage: PropTypes.shape({
    raw: PropTypes.string,
  }),
  logosSectionHeading: PropTypes.string,
  logos: PropTypes.arrayOf(PropTypes.shape({})),
  requireJobTitle: PropTypes.bool,
}

export default ContactSection

export const query = graphql`
  fragment ContactSection on ContentfulContactSection {
    id
    __typename
    label
    heading
    headingImage {
      gatsbyImageData(quality: 100)
      description
    }
    hubspotForm {
      formId
      trackSubmit: formSubmitTracking
    }
    leftSideRichText {
      raw
    }
    leftSideContactMessage {
      raw
    }
    successMessage {
      raw
    }
    logosSectionHeading
    logos {
      id
      title
      description
      gatsbyImageData(quality: 100, height: 36, layout: FIXED)
    }
  }
`
